"use client";

import { Portal } from "@ark-ui/react/portal";
import { createListCollection, Select as ArkSelect } from "@ark-ui/react/select";
import { IconCaretDown, IconMagnifyingGlass } from "@decibeltrade/react-icons";
import { Spinner } from "@decibeltrade/web-core/components";
import { focusRing, menuItemFocusRing } from "@decibeltrade/web-core/recipes";
import { type ForwardedRef, forwardRef, useMemo, useRef, useState } from "react";
import { css, cva, cx, type RecipeVariantProps } from "styled-system/css";
import { center, flex } from "styled-system/patterns";

import { formControl, type FormControlVariants } from "@/recipes/form-control";
import { menu, menuItem } from "@/recipes/menu";

import type { FormControlProps } from "./form-field";
import { Input } from "./input";

export type SelectVariant = NonNullable<RecipeVariantProps<typeof selectTrigger>>["variant"];

export interface SelectOption<TValue extends string> {
  /** The value to be emitted by the `onChange` callback */
  value: TValue;
  /** The label of the option if you don't want to render value directly */
  label?: string;
  /** If true, the option will not be selectable */
  disabled?: boolean;
  /** If true, the option will be rendered using the destructive variant */
  destructive?: boolean;
  /** A callback that runs when the option is hovered */
  onHover?: () => void;
  /** The icon to display next to the option */
  icon?: React.ReactNode;
}

export interface SelectProps<
  TValue extends string,
  TOption extends SelectOption<TValue> = SelectOption<TValue>,
> extends Partial<FormControlProps> {
  /** The style variant of the select menu trigger. */
  variant?: SelectVariant;
  /** The size of the select trigger and menu */
  size?: FormControlVariants["size"];
  /** The validation state of the select trigger */
  state?: FormControlVariants["state"];
  /** Placeholder to render in the trigger if value and label are undefined */
  placeholder?: string;
  /** The currently selected value */
  value?: TValue;
  /**
   * The label to display in the trigger if you don't want to render the current value's label.
   *
   * Takes a node rather than a string so a caller can style part of it — the deposit modal's
   * pickers render their "Asset:" / "Chain:" prefix in a dimmer colour than the value.
   */
  label?: React.ReactNode;
  /** The possible options that can be selected */
  options: TOption[] | readonly TOption[];
  /** The callback that runs each time an option is selected */
  onChange: (selectedOption: TValue) => void;
  /** Whether or not the select trigger is disabled */
  disabled?: boolean;
  /** Whether or not to display a spinner in the menu. Useful for when options are fetched from the server. */
  isMenuLoading?: boolean;
  /** Whether to display a fuzzy search bar at the top of the menu */
  hasSearch?: boolean;
  /** Whether to display the menu at the same width as the trigger */
  sameWidth?: boolean;
  /** Whether to render the select menu in a portal attached to the document body */
  portalled?: boolean;
  /** The name attribute of the underlying select element. Useful for form submission. */
  name?: string;
  /** Class name for applying additional styles to the select's trigger element */
  className?: string;
  /** TestId for the select */
  testId?: string;
  /** Accessible name for the trigger, for selects rendered without a visible label. */
  ariaLabel?: string;
  /**
   * Custom render function for menu items. If provided, this will be used instead of the default label rendering.
   */
  renderItem?: (option: TOption) => React.ReactNode;
}

function SelectImplementation<
  TValue extends string,
  TOption extends SelectOption<TValue> = SelectOption<TValue>,
>(
  {
    variant = "primary",
    size = "md",
    state = "default",
    placeholder,
    value,
    label,
    options,
    onChange,
    disabled,
    isMenuLoading,
    hasSearch,
    sameWidth = true,
    portalled = true,
    name,
    className,
    id,
    testId,
    ariaLabel,
    "aria-describedby": ariaDescribedBy,
    renderItem,
  }: SelectProps<TValue, TOption>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredOptionsList = useMemo(
    () =>
      options.filter((o) => {
        const optionText = (o.label ?? o.value).toLowerCase();
        return optionText.includes(searchQuery.toLowerCase());
      }),
    [searchQuery, options],
  );

  const filteredOptions = useMemo(
    () => createListCollection({ items: filteredOptionsList }),
    [filteredOptionsList],
  );

  const itemsByValue = useMemo(
    () => new Map(filteredOptions.items.map((item) => [item.value, item])),
    [filteredOptions.items],
  );

  const selectedOption = useMemo(
    () => (value ? options.find((o) => o.value === value) : undefined),
    [value, options],
  );

  const triggerContent = useMemo(() => {
    if (label !== undefined) {
      return label;
    }
    if (renderItem && selectedOption) {
      return renderItem(selectedOption);
    }
    return (
      <>
        {selectedOption?.icon}
        {selectedOption?.label ?? selectedOption?.value ?? null}
      </>
    );
  }, [label, renderItem, selectedOption]);

  const menuContent = (
    <ArkSelect.Positioner>
      <ArkSelect.Content asChild>
        <div ref={menuRef} className={menu({ size })}>
          {!!hasSearch && (
            <div className={css({ p: "8" })}>
              <Input
                ref={searchRef}
                size="sm"
                placeholder="Search"
                leadElement={
                  <IconMagnifyingGlass
                    className={css({ color: "icon.primary", h: "16", w: "16" })}
                  />
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.code === "ArrowDown") {
                    // Move focus to the first menu option
                    menuRef.current?.focus();
                  }
                  if (e.code === "Space") {
                    // Prevent pressing space in the input from selecting an option
                    e.stopPropagation();
                  }
                }}
              />
            </div>
          )}
          {!isMenuLoading &&
            filteredOptionsList.map((option) => {
              const item = itemsByValue.get(option.value);
              if (!item) return null;
              return (
                <ArkSelect.Item
                  key={option.value}
                  item={item}
                  aria-disabled={option.disabled}
                  onMouseEnter={option.onHover}
                  className={cx(menuItem({ size }), menuItemFocusRing())}
                >
                  <ArkSelect.ItemText
                    className={flex({ w: "full", minW: "0", gap: "4", alignItems: "center" })}
                  >
                    {renderItem ? (
                      renderItem(option)
                    ) : (
                      <>
                        {option.icon}
                        {option.label ?? option.value}
                      </>
                    )}
                  </ArkSelect.ItemText>
                </ArkSelect.Item>
              );
            })}
          {isMenuLoading && (
            <div className={center({ p: "32" })}>
              <Spinner size={size} variant="secondary" />
            </div>
          )}
        </div>
      </ArkSelect.Content>
    </ArkSelect.Positioner>
  );

  return (
    <ArkSelect.Root
      loopFocus
      ids={{ hiddenSelect: id }}
      name={name}
      value={value ? [value] : undefined}
      collection={filteredOptions}
      onValueChange={(e) => onChange(e.value[0] as TValue)}
      onOpenChange={(e) => {
        if (e.open && searchRef.current) {
          // Force focus to go to search input when menu is opened
          setTimeout(() => searchRef.current?.focus(), 0);
        }
        if (!e.open && searchQuery) {
          // Clear search when menu is closed
          // Wait a bit for exit animation to finish though
          setTimeout(() => setSearchQuery(""), 200);
        }
        setIsOpen(e.open);
      }}
      disabled={disabled}
      positioning={{ sameWidth, gutter: 4 }}
    >
      <ArkSelect.Control>
        <ArkSelect.Trigger
          ref={ref}
          data-active={isOpen || undefined}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          className={cx(
            formControl({ size, state, variant: "transparent" }),
            selectTrigger({ variant }),
            focusRing({ rounded: "350" }),
            className,
          )}
          data-testid={testId}
        >
          <ArkSelect.ValueText
            placeholder={placeholder}
            className={flex({
              textAlign: "left",
              flex: "1",
              gap: "4",
              "&:empty:before": {
                // Use unicode zero-width space character to maintain height when empty
                content: "'\u200b'",
              },
            })}
          >
            {triggerContent}
          </ArkSelect.ValueText>
          <ArkSelect.Indicator>
            <IconCaretDown data-open={isOpen || undefined} className={indicatorIcon({ size })} />
          </ArkSelect.Indicator>
        </ArkSelect.Trigger>
      </ArkSelect.Control>
      <ArkSelect.HiddenSelect />

      {portalled ? <Portal>{menuContent}</Portal> : menuContent}
    </ArkSelect.Root>
  );
}

const selectTrigger = cva({
  base: {
    cursor: "pointer",
  },
  variants: {
    variant: {
      primary: {},
      secondary: {
        borderColor: "transparent!",
        "&:hover, &[data-active]": {
          borderColor: "transparent!",
        },
      },
    },
  },
});

const indicatorIcon = cva({
  base: {
    color: "icon.primary",
    _open: { transform: "rotate(180deg)" },
    transitionPreset: "common",
  },
  variants: {
    size: {
      sm: { h: "16", w: "16" },
      md: { h: "16", w: "16" },
      lg: { h: "20", w: "20" },
    },
  },
});

// forwardRef does not allow for generic prop types so we'll need a bit of casting below :/

/**
 * A form control, that when clicked, opens a menu for selecting an option from a pre-defined list.
 */
export const Select = forwardRef(SelectImplementation) as <
  TValue extends string,
  TOption extends SelectOption<TValue> = SelectOption<TValue>,
>(
  props: SelectProps<TValue, TOption> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactElement;
